export interface IComment {
	id: number;
	comment: string;
	createdBy: string;
	createdAt: Date;
	updatedAt: Date;
}
/*testing github */
export class PatientComment implements IComment {
	id: number;
	comment: string;
	createdBy: string;
	createdAt: Date;
	updatedAt: Date;

	constructor(data: any) {
		this.id = data.id || 0;
		this.comment = data.comment || '';
		this.createdBy = data.creator_name || data.createdBy || '';
		this.createdAt = data.created_at ? new Date(data.created_at) : new Date();
		this.updatedAt = data.updated_at ? new Date(data.updated_at) : new Date();
	}

	toOriginalFormat(): any {
		return {
			comment: this.comment
		};
	}
}

/**
 * Category id for PST notes (mirrors NoteCategory::PST_NOTE on the backend).
 * PST notes are communications — profiles must not create one or convert a note
 * into one, and an existing PST note's category/subject are immutable.
 */
export const PST_NOTE_CATEGORY_ID = 4;

export interface INote {
	id: number;
	subject: string;
	note: string;
	critical: boolean;
	patientPopup: boolean;
	orderPopup: boolean;
	autoApplyToOrderComment: boolean;
	category: string;
	categoryId: number | null;
	createdBy: string;
	createdAt: Date;
	updatedAt: Date;
	noteType: string;
	lastEdited?: { by: string; at: string } | null;
	canEdit: boolean;
	// Source Order ID for order notes; null/undefined for patient notes.
	orderId?: number | null;
}

export class Notes implements INote {
	id: number;
	subject: string;
	note: string;
	critical: boolean;
	patientPopup: boolean;
	orderPopup: boolean;
	autoApplyToOrderComment: boolean;
	// Callers without the control (order notes, legacy edit route) must not send the key, or they would reset a stored true.
	private hasAutoApplyToOrderComment: boolean;
	category: string;
	categoryId: number;
	createdBy: string;
	createdAt: Date;
	updatedAt: Date;
	noteType: string;
	lastEdited?: { by: string; at: string } | null;
	canEdit: boolean;
	orderId: number | null;

	constructor(data: any) {
		this.id = data.id || 0;
		this.subject = data.subject || '';
		this.note = data.note || '';
		this.critical = data.critical == 1 ? true : false || false;
		this.patientPopup = data.patientPopup || data.patient_popup == 1 ? true : false || false;
		this.orderPopup = data.orderPopup || data.order_popup == 1 ? true : false || false;
		const autoApplyToOrderComment = data.autoApplyToOrderComment ?? data.auto_apply_to_order_comment;
		this.hasAutoApplyToOrderComment = autoApplyToOrderComment != null;
		this.autoApplyToOrderComment = !!autoApplyToOrderComment;
		this.category = data.category || '';
		this.categoryId = data.category_id || data.categoryId || null;
		this.createdBy = data.creator_name || '';
		// DOVE-2605 — created_at/updated_at are Pacific wall-clock strings with no offset, which
		// new Date() reads as the viewer's own local time. The *_iso fields carry the offset, so
		// every viewer sees their local time; the plain fields remain as a fallback.
		const createdAt = data.created_at_iso ?? data.created_at;
		const updatedAt = data.updated_at_iso ?? data.updated_at;
		this.createdAt = createdAt ? new Date(createdAt) : new Date();
		this.updatedAt = updatedAt ? new Date(updatedAt) : new Date();
		this.noteType = data.note_type || '';
		this.lastEdited = data.last_edited
			? { by: data.last_edited.by, at: data.last_edited.at }
			: (data.lastEdited ?? null);
		this.canEdit = data.can_edit ?? data.canEdit ?? false;
		this.orderId = data.order_id ?? data.orderId ?? null;
	}

	toOriginalFormat(): any {
		return {
			subject: this.subject,
			note: this.note,
			critical: this.critical,
			patient_popup: this.patientPopup,
			order_popup: this.orderPopup,
			...(this.hasAutoApplyToOrderComment ? { auto_apply_to_order_comment: this.autoApplyToOrderComment } : {}),
			category_id: this.categoryId,
			note_type: this.noteType
		};
	}
}

export interface IOrderReminderNote {
	id: number;
	source: string;
	sourceId: number;
	sourceName: string;
	subject: string;
	note: string;
	critical: boolean;
	categoryId: number | null;
	createdAt: Date | null;
}

export class OrderReminderNote implements IOrderReminderNote {
	id: number;
	source: string;
	sourceId: number;
	sourceName: string;
	subject: string;
	note: string;
	critical: boolean;
	categoryId: number | null;
	createdAt: Date | null;

	constructor(data: any) {
		this.id = data.id || 0;
		this.source = data.source || '';
		this.sourceId = data.source_id || data.sourceId || 0;
		this.sourceName = data.source_name || data.sourceName || '';
		this.subject = data.subject || 'Order Reminder';
		this.note = data.note || '';
		this.critical = data.critical === true || data.critical === 1;
		this.categoryId = data.category_id || data.categoryId || null;
		// DOVE-2605 — prefer the offset-carrying ISO value (see Notes above).
		const createdAt = data.created_at_iso ?? data.created_at;
		this.createdAt = createdAt ? new Date(createdAt) : null;
	}
}
